import { useEditorUI } from './ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  hexA,
  rectContains,
  rectsOverlap,
  strokeBBox,
  type BoardDoc,
  type Item,
  type Point,
  type Rect,
} from '@scribbleplot/shared';
import { CANVAS, color } from '../tokens';
import { useCamera } from './camera';
import { useBoard, type Tool } from './store';
import { ARROWS, announce, nudgeDistance, stepSelection } from './keyboard';
import { VisuallyHidden } from '../ui/VisuallyHidden';
import { CanvasItem, type ItemEvents } from './CanvasItem';
import { InlineEditor } from './InlineEditor';
import { CommentPins } from './Comments';
import { DropZone, EmptyHint, FrameDraft, Marquee, PeerCursors, ResizeHandles, VectorLayer } from './Overlays';
import { pinchDown, pinchMove, pinchReset, pinchUp, isPinching } from './pinch';
import {
  registerGestureHooks,
  startAssetDrag,
  startDrag,
  startDraw,
  startFrameDraft,
  startMarquee,
  startPan,
  startResize,
  type AssetDragPayload,
  type ResizeDir,
} from './gestures';
import { sendCursor } from './store';

/**
 * The infinite canvas.
 *
 * Two nested layers: a fixed viewport that owns input and the dot grid, and a
 * transformed world container holding everything in board coordinates. Panning
 * and zooming only touch the world container's transform, which the compositor
 * handles without re-laying out any items.
 */

export type CanvasProps = {
  accent: string;
  dotGrid: boolean;
  presence: boolean;
  authorName: string;
  onUploadFiles: (files: File[], at: Point) => void;
  /** Reports the asset-drag ghost so it can render above every panel. */
  onGhostChange: (ghost: { x: number; y: number; src: string } | null) => void;
};

/**
 * How far beyond the viewport to keep rendering, as a fraction of the viewport.
 *
 * The rendered set is only recomputed once the view escapes this window, so
 * ordinary panning changes nothing but a CSS transform — which is what keeps it
 * on the compositor rather than in React.
 */
const CULL_MARGIN = 0.5;

/** Grow a rectangle by a fraction of its own size on every side. */
function expand(rect: Rect, factor: number): Rect {
  const dx = rect.w * factor;
  const dy = rect.h * factor;
  return { x: rect.x - dx, y: rect.y - dy, w: rect.w + dx * 2, h: rect.h + dy * 2 };
}

/**
 * Items and strokes worth rendering.
 *
 * Anything selected or being edited is kept regardless of where it sits: its
 * resize handles and text editor are anchored to it, and they must not vanish
 * because the camera drifted.
 */
function cull(
  doc: BoardDoc,
  window: Rect | null,
  keep: ReadonlySet<string>,
): { items: Item[]; strokes: BoardDoc['strokes'] } {
  if (!window) return { items: doc.items, strokes: doc.strokes };
  return {
    items: doc.items.filter((it) => keep.has(it.id) || rectsOverlap(it, window)),
    strokes: doc.strokes.filter((s) => keep.has(s.id) || rectsOverlap(strokeBBox(s), window)),
  };
}

/** True while the space bar is held, which turns any tool into a pan tool. */
function useSpaceKey(): boolean {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (event.code === 'Space' || event.key === ' ') {
        event.preventDefault();
        setHeld(true);
      }
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.key === ' ') setHeld(false);
    };
    // Releasing outside the window would otherwise leave pan mode stuck on.
    const blur = () => setHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);
  return held;
}

export function Canvas({ accent, dotGrid, presence, authorName, onUploadFiles, onGhostChange }: CanvasProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const dragDepth = useRef(0);
  const [dropping, setDropping] = useState(false);

  const space = useSpaceKey();
  const cam = useCamera((s) => s.cam);
  const cameraApi = useCamera;

  const board = useBoard();
  const { doc, sel, tool, editing, canEdit, peers, marquee, frameDraft, drawPts, connectFrom, connLive } = board;

  const scale = cam.s;

  /* ---------------- camera binding & wheel ---------------- */

  useEffect(() => {
    if (board.boardId) cameraApi.getState().bind(board.boardId, viewportRef.current);
  }, [board.boardId, cameraApi]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    /**
     * Registered non-passively so the default page zoom can be prevented.
     * Ctrl/Cmd + wheel is the pinch-zoom gesture browsers synthesise from a
     * trackpad; a bare wheel pans, matching every other canvas tool.
     */
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        cameraApi.getState().zoomAt(Math.exp(-event.deltaY * 0.0022), event.clientX, event.clientY);
      } else {
        cameraApi.getState().panBy(-event.deltaX, -event.deltaY);
      }
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [cameraApi]);

  /* ---------------- gesture hook registration ---------------- */

  const panMinimap = useCallback((clientX: number, clientY: number) => {
    window.dispatchEvent(new CustomEvent('sp:minimap-pan', { detail: { clientX, clientY } }));
  }, []);

  useEffect(() => {
    registerGestureHooks({ setGhost: onGhostChange, panMinimap, accent: () => accent });
  }, [onGhostChange, panMinimap, accent]);

  // Touch state is module-level, so it must not survive a remount.
  useEffect(() => pinchReset, []);

  /* ---------------- background interaction ---------------- */

  const isBackground = (event: React.MouseEvent | React.PointerEvent) => {
    const target = event.target as HTMLElement | null;
    return Boolean(target?.dataset?.bg);
  };

  /**
   * A touch that may still turn into a pinch.
   *
   * With a creation tool armed, acting on `pointerdown` would place an item the
   * instant the first finger lands — so starting a two-finger zoom would leave a
   * stray sticky behind. For touch, creation waits for the finger to lift.
   */
  const pendingTap = useRef<{ id: number; x: number; y: number; tool: Tool } | null>(null);

  const onPointerDown = (event: React.PointerEvent) => {
    // A second finger turns this into a camera gesture, whatever the first one
    // started; suppress content handling for the rest of the touch.
    if (pinchDown(event) || isPinching()) {
      // Abandon anything the first finger had begun.
      pendingTap.current = null;
      board.setDrawPts(null);
      board.setFrameDraft(null);
      board.setMarquee(null);
      return;
    }

    board.clearOverlays();

    // Middle-drag, space-drag, and the hand tool all pan.
    if (event.button === 1 || space || tool === 'hand') {
      startPan(event);
      return;
    }
    if (event.button !== 0) return;

    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);

    if (!canEdit || tool === 'select') {
      startMarquee(wx, wy, event.shiftKey || useEditorUI.getState().multiSelect);
      return;
    }

    // Defer tap-to-create on touch until the finger lifts (see pendingTap).
    const tapToCreate = tool === 'sticky' || tool === 'text' || tool === 'link' || tool === 'comment';
    if (event.pointerType === 'touch' && tapToCreate) {
      pendingTap.current = { id: event.pointerId, x: event.clientX, y: event.clientY, tool };
      return;
    }

    switch (tool) {
      case 'sticky':
        board.addSticky(wx, wy);
        break;
      case 'text':
        board.addText(wx, wy);
        break;
      case 'link':
        board.addLink(wx, wy);
        break;
      case 'frame':
        startFrameDraft(wx, wy);
        break;
      case 'draw':
        startDraw(wx, wy);
        break;
      case 'connect':
        board.setConnect(null, null);
        break;
      case 'comment':
        board.addComment(wx, wy, authorName);
        break;
    }
  };

  /** Complete a deferred touch tap, unless it became a pinch or a drag. */
  const onPointerUp = (event: React.PointerEvent) => {
    const tap = pendingTap.current;
    pinchUp(event);

    if (!tap || tap.id !== event.pointerId) return;
    pendingTap.current = null;
    if (isPinching()) return;
    // A finger that travelled was a pan or a scroll, not a tap.
    if (Math.hypot(event.clientX - tap.x, event.clientY - tap.y) > 12) return;

    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    switch (tap.tool) {
      case 'sticky':
        board.addSticky(wx, wy);
        break;
      case 'text':
        board.addText(wx, wy);
        break;
      case 'link':
        board.addLink(wx, wy);
        break;
      case 'comment':
        board.addComment(wx, wy, authorName);
        break;
      default:
        break;
    }
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (pinchMove(event)) return;
    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    if (tool === 'connect' && connectFrom) board.setConnect(connectFrom, [wx, wy]);
    if (presence) sendCursor({ x: wx, y: wy });
  };

  const onDoubleClick = (event: React.MouseEvent) => {
    if (!canEdit || tool !== 'select' || !isBackground(event)) return;
    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    board.addSticky(wx, wy);
  };

  const onContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    board.setMenu({ sx: event.clientX, sy: event.clientY, id: null, wx, wy });
  };

  /* ---------------- item interaction ---------------- */

  const onItemPointerDown = (item: Item, event: React.PointerEvent) => {
    if (pinchDown(event) || isPinching()) return;
    event.stopPropagation();
    board.setMenu(null);
    if (editing && editing !== item.id) board.setEditing(null);

    if (event.button === 1 || space || tool === 'hand') {
      startPan(event);
      return;
    }

    if (tool === 'connect' && canEdit) {
      if (event.button !== 0) return;
      if (!connectFrom) board.setConnect(item.id, null);
      else if (connectFrom !== item.id) {
        board.addConnector(connectFrom, item.id);
        board.setConnect(null, null);
      }
      return;
    }

    // Any other creation tool should act on the canvas beneath the item.
    if (tool !== 'select' && canEdit) {
      onPointerDown(event);
      return;
    }

    // Work out the selection this click implies before starting the drag.
    let next = sel;
    let reduceTo: string | null = null;
    if (event.shiftKey || useEditorUI.getState().multiSelect) {
      next = sel.includes(item.id) ? sel.filter((id) => id !== item.id) : [...sel, item.id];
    } else if (!sel.includes(item.id)) {
      next = [item.id];
    } else if (sel.length > 1) {
      // Clicking inside a multi-selection narrows it, but only on release, so
      // dragging the group still works.
      reduceTo = item.id;
    }
    board.setSel(next);

    if (!canEdit || event.button !== 0) return;

    /**
     * Raise what was just grabbed, so it comes to the front. Frames stay in
     * their own band underneath everything else.
     *
     * Dispatched silently: selecting something is not an edit, and recording a
     * z-order bump per click would fill the undo history with noise. The op is
     * skipped entirely when the selection is already on top, which is the common
     * case and saves a round trip on every click.
     */
    if (item.type === 'frame') {
      const topFrame = board.nextFrameZ() - 1;
      if (item.z < topFrame) {
        board.applySilent([{ t: 'set', c: 'items', p: [{ id: item.id, patch: { z: topFrame + 1 } }] }]);
      }
    } else {
      const top = board.nextZ() - 1;
      const raising = doc.items.filter((it) => next.includes(it.id) && it.type !== 'frame');
      const alreadyOnTop = raising.length === 1 && raising[0].z >= top;
      if (raising.length && !alreadyOnTop) {
        let z = top + 1;
        board.applySilent([{ t: 'set', c: 'items', p: raising.map((it) => ({ id: it.id, patch: { z: z++ } })) }]);
      }
    }

    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    startDrag(item, next, wx, wy, reduceTo);
  };

  const onItemDoubleClick = (item: Item, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canEdit) return;
    if (item.type === 'group') board.ungroup(item.id);
    else if (item.type !== 'image') board.setEditing(item.id);
  };

  const onItemContextMenu = (item: Item, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEdit) return;
    if (!sel.includes(item.id)) board.setSel([item.id]);
    board.setMenu({ sx: event.clientX, sy: event.clientY, id: item.id, wx: 0, wy: 0 });
  };

  const onStrokeDown = (id: string, event: React.PointerEvent) => {
    event.stopPropagation();
    if (tool !== 'select') {
      onPointerDown(event);
      return;
    }
    const next =
      event.shiftKey || useEditorUI.getState().multiSelect
        ? sel.includes(id)
          ? sel.filter((s) => s !== id)
          : [...sel, id]
        : sel.includes(id)
          ? sel
          : [id];
    board.setSel(next);
    if (!canEdit || event.button !== 0) return;
    const [wx, wy] = cameraApi.getState().toWorld(event.clientX, event.clientY);
    // Strokes have no rect of their own; a synthetic anchor keeps startDrag simple.
    startDrag(
      { id, type: 'sticky', x: 0, y: 0, w: 0, h: 0, z: 0, color: '#000000', text: '' } as Item,
      next,
      wx,
      wy,
      null,
    );
  };

  const onStrokeContext = (id: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canEdit) return;
    if (!sel.includes(id)) board.setSel([id]);
    board.setMenu({ sx: event.clientX, sy: event.clientY, id, wx: 0, wy: 0, isStroke: true });
  };

  const onResizeGrab = (dir: ResizeDir, event: React.PointerEvent) => {
    event.stopPropagation();
    const item = board.itemById(sel[0]);
    if (item && canEdit) startResize(dir, item);
  };

  const itemEvents = useRef<ItemEvents>({
    down: onItemPointerDown,
    double: onItemDoubleClick,
    context: onItemContextMenu,
  });
  itemEvents.current = { down: onItemPointerDown, double: onItemDoubleClick, context: onItemContextMenu };

  /* ---------------- keyboard ---------------- */

  /**
   * Arrow nudges are grouped into one undo step per burst.
   *
   * Holding an arrow key repeats quickly; recording each repeat separately would
   * mean pressing undo forty times to put something back where it was.
   */
  const nudging = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Set by Escape: the next Tab passes straight through instead of re-entering
   * the object cycle. Without it, Escape would deselect and the very next Tab
   * would select the first object again — a trap with extra steps.
   */
  const tabReleased = useRef(false);
  const endNudge = useCallback(() => {
    if (!nudging.current) return;
    clearTimeout(nudging.current);
    nudging.current = null;
    useBoard.getState().endGesture();
  }, []);

  const nudge = (dx: number, dy: number) => {
    const store = useBoard.getState();
    const moving = store.doc.items.filter((item) => store.sel.includes(item.id));
    if (!moving.length) return;

    if (!nudging.current) store.beginGesture();
    else clearTimeout(nudging.current);
    // Idle for a moment and the burst is closed off as a single history entry.
    nudging.current = setTimeout(endNudge, 400);

    store.gestureOps([
      {
        t: 'set',
        c: 'items',
        p: moving.map((item) => ({ id: item.id, patch: { x: item.x + dx, y: item.y + dy } })),
      },
    ]);
  };

  useEffect(() => endNudge, [endNudge]);

  /**
   * Return focus to the canvas when an inline editor closes.
   *
   * Without this the textarea unmounts and focus falls to the document body, so
   * the next arrow key or Tab goes nowhere — the keyboard flow breaks at exactly
   * the moment someone finishes typing and wants to move what they just made.
   */
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) viewportRef.current?.focus();
    wasEditing.current = Boolean(editing);
  }, [editing]);

  /**
   * Keys handled only while focus is inside the canvas.
   *
   * Tab is the notable one: taking it over is what makes the objects reachable,
   * but it would strand a keyboard user if there were no way out — Escape drops
   * the selection and hands Tab back to the page.
   */
  const onKeyDown = (event: React.KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;

    const store = useBoard.getState();

    if (event.key === 'Escape') {
      // Hand Tab back to the page. The board's global handler clears the
      // selection; this just remembers that the cycle was left deliberately.
      tabReleased.current = true;
      return;
    }

    if (event.key === 'Tab') {
      if (tabReleased.current) {
        tabReleased.current = false;
        return; // Let focus move on to the next control.
      }
      const next = stepSelection(store.doc, store.sel, event.shiftKey);
      if (next === null) {
        // Ran off either end: drop the selection and let focus continue.
        if (store.sel.length) store.setSel([]);
        return;
      }
      event.preventDefault();
      store.setSel([next]);
      const item = store.itemById(next);
      if (item) cameraApi.getState().centerOn(item);
      return;
    }

    // Any other key means the user is working in the canvas again.
    tabReleased.current = false;

    const arrow = ARROWS[event.key];
    if (arrow && store.sel.length && canEdit) {
      event.preventDefault();
      const step = nudgeDistance(event.shiftKey);
      nudge(arrow[0] * step, arrow[1] * step);
      return;
    }

    if (event.key === 'Enter' && canEdit) {
      event.preventDefault();
      const selected = store.sel.length === 1 ? store.itemById(store.sel[0]) : undefined;

      if (selected) {
        // Enter opens the selection, matching what double-click does.
        if (selected.type === 'group') store.ungroup(selected.id);
        else if (selected.type !== 'image') store.setEditing(selected.id);
        return;
      }

      // Nothing selected: place the armed tool at the middle of the view, which
      // is the keyboard equivalent of clicking there.
      const viewport = cameraApi.getState().viewport;
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const [wx, wy] = cameraApi.getState().toWorld(rect.left + rect.width / 2, rect.top + rect.height / 2);
      switch (store.tool) {
        case 'sticky':
        case 'select':
          store.addSticky(wx, wy);
          break;
        case 'text':
          store.addText(wx, wy);
          break;
        case 'link':
          store.addLink(wx, wy);
          break;
        case 'comment':
          store.addComment(wx, wy, authorName);
          break;
        default:
          break;
      }
    }
  };

  /* ---------------- file drops ---------------- */

  const onDragEnter = (event: React.DragEvent) => {
    if (!canEdit) return;
    event.preventDefault();
    dragDepth.current++;
    setDropping(true);
  };
  const onDragOver = (event: React.DragEvent) => {
    if (canEdit) event.preventDefault();
  };
  const onDragLeave = () => {
    // Nested elements each fire leave; only clear when the last one does.
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropping(false);
  };
  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDropping(false);
    if (!canEdit) return;
    const files = [...(event.dataTransfer?.files ?? [])].filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    onUploadFiles(files, cameraApi.getState().toWorld(event.clientX, event.clientY));
  };

  /* ---------------- viewport culling ---------------- */

  const [renderWindow, setRenderWindow] = useState<Rect | null>(null);

  useEffect(() => {
    const view = cameraApi.getState().viewRect();
    if (!view) return;
    const ideal = expand(view, CULL_MARGIN);

    /**
     * Recompute when the camera has left the window (panned away), or when the
     * window has become far larger than needed (zoomed in, which shrinks the
     * view inside a window sized for a wider shot). Without the second case the
     * window would stay at whatever the most zoomed-out view required and
     * nothing would ever be culled.
     *
     * The 2× slack is what keeps ordinary panning and small zoom steps from
     * re-filtering the list on every frame.
     */
    const escaped = !renderWindow || !rectContains(renderWindow, view);
    const oversized = renderWindow !== null && (renderWindow.w > ideal.w * 2 || renderWindow.h > ideal.h * 2);
    if (escaped || oversized) setRenderWindow(ideal);
  }, [cam, renderWindow, cameraApi]);

  /* ---------------- derived render state ---------------- */

  const selectedSet = useMemo(() => new Set(sel), [sel]);

  /** Kept on screen no matter where the camera is. */
  const pinned = useMemo(() => {
    const ids = new Set(sel);
    if (editing) ids.add(editing);
    return ids;
  }, [sel, editing]);

  const visible = useMemo(() => cull(doc, renderWindow, pinned), [doc, renderWindow, pinned]);
  const editingItem = editing ? board.itemById(editing) : undefined;
  const soleSelection = sel.length === 1 ? board.itemById(sel[0]) : undefined;

  const cursor =
    space || tool === 'hand' ? 'grab' : tool === 'text' ? 'text' : tool === 'select' ? 'default' : 'crosshair';

  const gridPitch = CANVAS.gridSize * scale;
  const showGrid = dotGrid && gridPitch > CANVAS.gridMinScreenPitch;

  return (
    <div style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', background: color.canvas }}>
      <div
        ref={viewportRef}
        data-bg="1"
        role="application"
        aria-label="Board canvas. Tab to step through objects, arrow keys to move them, Enter to edit, Escape to deselect."
        // Focusable so the objects can be reached without a pointer.
        tabIndex={0}
        onKeyDown={onKeyDown}
        onFocus={() => {
          // Arriving at the canvas re-arms the object cycle, so Escape releases
          // Tab once rather than disabling it until a reload.
          tabReleased.current = false;
        }}
        style={{ position: 'absolute', inset: 0, touchAction: 'none', cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={(event) => {
          pendingTap.current = null;
          pinchUp(event);
        }}
        onPointerLeave={() => presence && sendCursor(null)}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* The grid is painted in screen space and offset by the camera, which
            is far cheaper than tiling thousands of elements in the world. */}
        <div
          data-bg="1"
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: showGrid ? 'radial-gradient(rgba(255,255,255,.065) 1px, transparent 1.2px)' : 'none',
            backgroundSize: `${gridPitch}px ${gridPitch}px`,
            backgroundPosition: `${cam.x}px ${cam.y}px`,
          }}
        />

        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            transform: `translate(${cam.x}px,${cam.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
        >
          {visible.items.map((item) => (
            <CanvasItem
              key={item.id}
              item={item}
              selected={selectedSet.has(item.id) || connectFrom === item.id}
              accent={accent}
              scale={scale}
              editing={editing === item.id}
              events={itemEvents}
            />
          ))}

          <VectorLayer
            doc={doc}
            strokes={visible.strokes}
            sel={sel}
            accent={accent}
            scale={scale}
            liveStroke={drawPts}
            connectFrom={connectFrom}
            connLive={connLive}
            onStrokeDown={onStrokeDown}
            onStrokeContext={onStrokeContext}
          />

          {marquee && (marquee.w > 2 || marquee.h > 2) && <Marquee rect={marquee} accent={accent} scale={scale} />}
          {frameDraft && <FrameDraft rect={frameDraft} accent={accent} />}

          <CommentPins accent={accent} scale={scale} authorName={authorName} />

          {presence && <PeerCursors peers={peers} scale={scale} />}

          {soleSelection && canEdit && !editing && (
            <ResizeHandles rect={soleSelection} accent={accent} scale={scale} onGrab={onResizeGrab} />
          )}

          {editingItem && canEdit && (
            <InlineEditor
              item={editingItem}
              accent={accent}
              scale={scale}
              onChange={(patch) => board.updateItem(editingItem.id, patch)}
              onClose={() => board.setEditing(null)}
            />
          )}
        </div>

        {dropping && <DropZone accent={accent} />}
      </div>

      {!doc.items.length && !doc.strokes.length && <EmptyHint canEdit={canEdit} />}

      {/* Tells a screen reader what just became selected. */}
      <VisuallyHidden live="polite" name="selection">
        {announce(doc, sel)}
      </VisuallyHidden>
    </div>
  );
}

/** Re-exported so panels can start an asset drag without importing gestures. */
export function beginAssetDrag(payload: AssetDragPayload, event: React.PointerEvent): void {
  event.preventDefault();
  startAssetDrag(payload, event);
}
